import React from "react";

const sections = [
    { id: "who-we-are", title: "1. Who We Are" },
    { id: "info-we-collect", title: "2. Information We Collect" },
    { id: "google-data", title: "3. Use of Google User Data" },
    { id: "how-we-use", title: "4. How We Use Information" },
    { id: "data-sharing", title: "5. Data Sharing" },
    { id: "data-retention", title: "6. Data Retention" },
    { id: "data-security", title: "7. Data Security" },
    { id: "your-choices", title: "8. Your Choices" },
    { id: "children", title: "9. Children's Privacy" },
    { id: "changes", title: "10. Changes to This Policy" },
    { id: "contact", title: "11. Contact Us" },
];

const styles = {
    page: {
        height: "100vh",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        background: "#f5f6f8",
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
        color: "#1f2937",
    },
    hero: {
        background: "linear-gradient(135deg, #111827 0%, #1f2a44 100%)",
        color: "#fff",
        padding: "56px 24px 40px",
        textAlign: "center",
    },
    heroBadge: {
        display: "inline-block",
        fontSize: 12,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        background: "rgba(255,255,255,0.12)",
        padding: "4px 12px",
        borderRadius: 999,
        marginBottom: 14,
    },
    heroTitle: {
        fontSize: 32,
        fontWeight: 700,
        margin: 0,
    },
    heroSub: {
        marginTop: 10,
        fontSize: 14,
        color: "#c7cbd4",
    },
    container: {
        maxWidth: 880,
        margin: "-28px auto 80px",
        padding: "0 20px",
        display: "flex",
        gap: 32,
        alignItems: "flex-start",
    },
    card: {
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08)",
        padding: "36px 40px 44px",
        flex: 1,
        minWidth: 0,
    },
    toc: {
        width: 220,
        flexShrink: 0,
        position: "sticky",
        top: 28,
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08)",
        padding: "20px 18px",
    },
    tocTitle: {
        fontSize: 12,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "#9ca3af",
        marginBottom: 10,
        fontWeight: 600,
    },
    tocLink: {
        display: "block",
        fontSize: 13.5,
        color: "#4b5563",
        textDecoration: "none",
        padding: "6px 0",
        borderLeft: "2px solid transparent",
        paddingLeft: 10,
    },
    lead: {
        fontSize: 15.5,
        color: "#374151",
        marginBottom: 8,
    },
    h2: {
        fontSize: 19,
        fontWeight: 700,
        marginTop: 38,
        marginBottom: 12,
        color: "#111827",
        scrollMarginTop: 24,
    },
    p: {
        fontSize: 15,
        color: "#374151",
        lineHeight: 1.7,
        margin: "0 0 14px",
    },
    ul: {
        paddingLeft: 20,
        margin: "0 0 14px",
    },
    li: {
        fontSize: 15,
        color: "#374151",
        lineHeight: 1.7,
        marginBottom: 6,
    },
    code: {
        background: "#f3f4f6",
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        padding: "2px 6px",
        fontSize: 13.5,
        color: "#b91c1c",
    },
    a: {
        color: "#2563eb",
        textDecoration: "none",
        borderBottom: "1px solid rgba(37,99,235,0.35)",
    },
    scopeBox: {
        background: "#fafaf9",
        border: "1px solid #e5e7eb",
        borderLeft: "4px solid #2563eb",
        borderRadius: 8,
        padding: "14px 18px",
        margin: "0 0 16px",
    },
    contactBox: {
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "20px 24px",
        marginTop: 8,
    },
    divider: {
        border: "none",
        borderTop: "1px solid #eef0f3",
        margin: "36px 0 0",
    },
    footer: {
        textAlign: "center",
        fontSize: 12.5,
        color: "#9ca3af",
        padding: "28px 20px 0",
    },
};

const Section = ({ id, title, children }) => (
    <section id={id}>
        <h2 style={styles.h2}>{title}</h2>
        {children}
    </section>
);

const PrivacyPolicy = () => {
    return (
        <div style={styles.page}>
            <div style={styles.hero}>
                <span style={styles.heroBadge}>Trackleads · Hexbis</span>
                <h1 style={styles.heroTitle}>Privacy Policy</h1>
                <p style={styles.heroSub}>Effective date: August 31, 2026</p>
            </div>

            <div style={styles.container}>
                <div style={styles.card}>
                    <p style={styles.lead}>
                        This Privacy Policy explains how <strong>Trackleads</strong> ("we", "our", "the
                        app"), a product of <strong>Hexbis</strong>, collects, uses, and protects
                        information when you use our application, including features that connect to your
                        Google Account.
                    </p>

                    <hr style={styles.divider} />

                    <Section id="who-we-are" title="1. Who We Are">
                        <p style={styles.p}>
                            Trackleads is a customer relationship management (CRM) platform built by Hexbis
                            for organizations to manage leads, teams, and internal workflows. This policy
                            applies to all users of Trackleads, including team members invited to an
                            organization's workspace.
                        </p>
                    </Section>

                    <Section id="info-we-collect" title="2. Information We Collect">
                        <ul style={styles.ul}>
                            <li style={styles.li}>
                                <strong>Account information:</strong> name, email address, organization
                                details, and role provided during signup or invitation.
                            </li>
                            <li style={styles.li}>
                                <strong>Usage data:</strong> actions taken within the CRM (leads, pipeline
                                stages, quotations, reports) associated with your account.
                            </li>
                            <li style={styles.li}>
                                <strong>Google Account information</strong> (only if you connect Google
                                Sign-In or Gmail features): your Google profile (name, email) for
                                authentication, and, where you explicitly authorize it, permission to send
                                emails on your behalf.
                            </li>
                        </ul>
                    </Section>

                    <Section id="google-data" title="3. Use of Google User Data">
                        <p style={styles.p}>Trackleads requests the following Google OAuth scope:</p>
                        <div style={styles.scopeBox}>
                            <code style={styles.code}>
                                https://www.googleapis.com/auth/gmail.send
                            </code>
                            <p style={{ ...styles.p, marginTop: 8, marginBottom: 0 }}>
                                Used only to send invitation emails to team members on your behalf, at your
                                explicit request (for example, when you click "Send Invite" inside the
                                app).
                            </p>
                        </div>
                        <p style={styles.p}>
                            We do <strong>not</strong> read, store, or share the content of your Gmail
                            inbox. We do not access any emails other than the single invitation message the
                            app composes and sends when you initiate that action. Access tokens obtained
                            via Google Sign-In are used only for the duration of the requested action and
                            are not stored on our servers beyond what is required to complete that request.
                        </p>
                        <p style={styles.p}>
                            Our use and transfer of information received from Google APIs adheres to the{" "}
                            <a
                                href="https://developers.google.com/terms/api-services-user-data-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={styles.a}
                            >
                                Google API Services User Data Policy
                            </a>
                            , including the Limited Use requirements.
                        </p>
                    </Section>

                    <Section id="how-we-use" title="4. How We Use Information">
                        <ul style={styles.ul}>
                            <li style={styles.li}>
                                To provide and operate core CRM functionality (leads, pipeline, reporting,
                                team management).
                            </li>
                            <li style={styles.li}>To authenticate users via Google Sign-In.</li>
                            <li style={styles.li}>
                                To send invitation emails on behalf of a user when explicitly requested.
                            </li>
                            <li style={styles.li}>To maintain security and prevent misuse of the platform.</li>
                        </ul>
                    </Section>

                    <Section id="data-sharing" title="5. Data Sharing">
                        <p style={styles.p}>We do not sell your data. Information is shared only:</p>
                        <ul style={styles.ul}>
                            <li style={styles.li}>
                                Within your own organization's workspace, as intended by the app's
                                team-management features.
                            </li>
                            <li style={styles.li}>
                                With service providers strictly necessary to operate the app (e.g.,
                                hosting), under confidentiality obligations.
                            </li>
                            <li style={styles.li}>When required by law.</li>
                        </ul>
                    </Section>

                    <Section id="data-retention" title="6. Data Retention">
                        <p style={styles.p}>
                            Account and usage data is retained for as long as your organization's account
                            is active. You may request deletion of your data by contacting us using the
                            details below.
                        </p>
                    </Section>

                    <Section id="data-security" title="7. Data Security">
                        <p style={styles.p}>
                            We use industry-standard measures, including encryption of sensitive data in
                            transit and at rest, to protect your information from unauthorized access.
                        </p>
                    </Section>

                    <Section id="your-choices" title="8. Your Choices">
                        <ul style={styles.ul}>
                            <li style={styles.li}>
                                You can revoke Trackleads' access to your Google Account at any time via
                                your{" "}
                                <a
                                    href="https://myaccount.google.com/permissions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.a}
                                >
                                    Google Account permissions page
                                </a>
                                .
                            </li>
                            <li style={styles.li}>
                                You can request access to, correction of, or deletion of your personal data
                                by contacting us.
                            </li>
                        </ul>
                    </Section>

                    <Section id="children" title="9. Children's Privacy">
                        <p style={styles.p}>
                            Trackleads is a business tool not directed at children, and we do not knowingly
                            collect information from individuals under 18.
                        </p>
                    </Section>

                    <Section id="changes" title="10. Changes to This Policy">
                        <p style={styles.p}>
                            We may update this Privacy Policy from time to time. Material changes will be
                            reflected by updating the "Effective date" above.
                        </p>
                    </Section>

                    <Section id="contact" title="11. Contact Us">
                        <p style={styles.p}>
                            If you have questions about this Privacy Policy or your data, contact us at:
                        </p>
                        <div style={styles.contactBox}>
                            <p style={{ ...styles.p, marginBottom: 4 }}>
                                <strong>Hexbis</strong>
                            </p>
                            <p style={{ ...styles.p, marginBottom: 0 }}>
                                Email:{" "}
                                <a href="mailto:alisha.hexbis@gmail.com" style={styles.a}>
                                    alisha.hexbis@gmail.com
                                </a>
                            </p>
                        </div>
                    </Section>
                </div>
            </div>

            <p style={styles.footer}>© 2026 Hexbis · Trackleads</p>
        </div>
    );
};

export default PrivacyPolicy;
